import { BaseComponent } from './BaseComponent.js';
import { toggleFavorite, isFavorite, getCocktailId } from '../cocktail-state.js';
import { 
  TOAST_DURATION, 
  SMALL_ICON_SIZE, 
  COCKTAIL_EVENTS 
} from '../constants.js';
import { ContentProcessor, AsyncErrorHandler } from '../utilities.js';
import { Validator, ValidationHelpers, ValidationError } from '../validation.js';
import { ErrorBoundary } from '../errorBoundary.js';
import { cocktailRouter } from '../router.js';
import { performanceMonitor } from '../performance.js';

class CocktailCard extends BaseComponent {
  constructor() {
    super('CocktailCard');
    this._cocktail = null;
    this._collapsed = false;
    this._index = null;
    this._rendered = false;
    this._elements = {}; // Cache DOM references
    this._lastState = {}; // Track last rendered state
  }

  static get observedAttributes() {
    return ['collapsed'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'collapsed') {
      this._collapsed = newValue === 'true';
      // Use CSS-based toggle instead of full re-render for better performance
      this._updateCollapseState();
    }
  }

  set cocktailData(data) {
    this._log.blue('Setting cocktail data:', data);
    this._log.blue('Cocktail fields:', Object.keys(data || {}));
    
    // Validate cocktail data using ValidationHelpers
    const validatedCocktail = ValidationHelpers.safeCocktail(data);
    
    if (!validatedCocktail && data) {
      this._log.yellow('Invalid cocktail data provided to', this.tagName);
      
      // Report validation error to error boundary
      ErrorBoundary.reportError(
        new ValidationError('Invalid cocktail data provided to component', 'cocktailData', data),
        this._componentName || 'CocktailCard'
      );
      
      // Show error state instead of failing silently
      this._cocktail = null;
      this.renderValidationError();
      return;
    }
    
    this._cocktail = validatedCocktail;
    this.render();
  }

  get cocktailData() {
    return this._cocktail;
  }

  set index(value) {
    this._index = value;
    this.dataset.index = value;
  }

  get index() {
    return this._index;
  }

  _initialize() {
    this.addManagedEventListener(this, 'click', this.handleCardClick);
    this.render();
    this.setupReactiveFavorites();
  }



  handleCardClick = (event) => {
    this._log.blue('Card clicked!', event.target);
    
    const favoriteButton = event.target.closest('.favorite-button');
    if (favoriteButton) {
      this._log.blue('Favorite button clicked!');
      event.stopPropagation();
      this.toggleFavorite();
      return;
    }

    const cardTitle = event.target.closest('.card-title');
    if (cardTitle) {
      this._log.blue('Card title clicked!');
      event.stopPropagation();
      this.openModal();
      return;
    }

    this._log.blue('Other area clicked');
  }

  openModal() {
    if (!this._cocktail) return;
    
    this._log.green('Opening modal for:', this._cocktail.name, 'via router');
    
    // Use router navigation instead of direct event dispatch
    cocktailRouter.navigateToCocktail(this._cocktail);
  }

  toggleFavorite() {
    this._log.blue('toggleFavorite called');
    this._log.blue('Cocktail:', this._cocktail);
    
    if (!this._cocktail) {
      this._log.yellow('No cocktail data');
      return;
    }
    
    const cocktailId = getCocktailId(this._cocktail);
    this._log.blue('Using cocktail ID:', cocktailId);
    
    if (!cocktailId) {
      this._log.yellow('No valid ID found - cocktail must have ID or idDrink field');
      return;
    }
    
    const isFavorited = toggleFavorite(cocktailId);
    this._log.blue('Result:', isFavorited);
    
    // Show toast notification
    const message = isFavorited ? 'Added to favorites!' : 'Removed from favorites!';
    this.showToast(message);
    
    this._log.green('Toggled favorite for:', this._cocktail.name, 'Now favorited:', isFavorited);
  }

  setupReactiveFavorites() {
    // Set up reactive effect to update favorite button when favorites change
    if (!this._cocktail) return;
    
    const cocktailId = getCocktailId(this._cocktail);
    if (!cocktailId) return;
    
    // Use managedEffect to automatically track cleanup
    this.addManagedEffect(() => {
      const isFav = isFavorite(cocktailId);
      this.updateFavoriteButton(isFav);
    });
  }

  updateFavoriteButton(isFavorited) {
    this._updateFavoriteButton(isFavorited);
  }
  
  _updateFavoriteButton(isFavorited) {
    // Use cached elements if available, otherwise query
    const favoriteButton = this._elements.favoriteButton || this.querySelector('.favorite-button');
    if (!favoriteButton) return;
    
    const svg = this._elements.favoriteSvg || favoriteButton.querySelector('svg');
    if (svg) {
      svg.setAttribute('fill', isFavorited ? 'currentColor' : 'none');
    }
    
    favoriteButton.classList.toggle('favorited', isFavorited);
    favoriteButton.title = isFavorited ? 'Remove from favorites' : 'Add to favorites';
    favoriteButton.setAttribute('aria-pressed', isFavorited);
  }

  async copyToClipboard() {
    if (!this._cocktail) return;

    const text = `${this._cocktail.name}\n\nIngredients:\n${this._cocktail.ingredients || 'N/A'}\n\nInstructions:\n${this._cocktail.instructions || 'N/A'}`;
    
    const result = await AsyncErrorHandler.clipboard(
      text,
      this,
      'Cocktail copied to clipboard!'
    );
    
    if (result.success) {
      this._log.green('Cocktail copied to clipboard:', this._cocktail.name);
      
      // Dispatch event for stats tracking
      document.dispatchEvent(new CustomEvent(COCKTAIL_EVENTS.COCKTAIL_COPIED, {
        detail: { cocktail: this._cocktail }
      }));
    }
  }

  showToast(message) {
    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, TOAST_DURATION);
  }

  renderValidationError() {
    this.innerHTML = `
      <div class="cocktail-card validation-error">
        <div class="error-state">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <h3>Invalid Cocktail Data</h3>
          <p>This cocktail could not be displayed due to invalid data.</p>
        </div>
      </div>
    `;
  }

  render() {
    try {
      if (!this._cocktail) {
        this._clearContent();
        return;
      }

      const { name, category, glass, alcoholic, instructions, ingredients, image } = this._cocktail;
      
      // Use the centralized ID function
      const cocktailId = getCocktailId(this._cocktail);
      const isFavorited = cocktailId ? isFavorite(cocktailId) : false;
      
      // Build current state for comparison (excluding collapsed since it's now CSS-based)
      const currentState = {
        name,
        category,
        glass,
        alcoholic,
        instructions,
        ingredients,
        image,
        isFavorited
      };
      
      // Check if we need initial render or if state changed significantly
      if (!this._lastRenderedState || this._needsFullRender(currentState)) {
        this._performFullRender(currentState);
      } else {
        this._performIncrementalUpdate(currentState);
      }
      
      // Apply collapse state using CSS (no re-render needed)
      this._updateCollapseState();
      
      // Store state for next comparison
      this._lastRenderedState = { ...currentState };
      
    } catch (error) {
      this._log.red('Error rendering cocktail card:', error);
      this._componentName && ErrorBoundary.reportError(error, this._componentName);
      
      // Show fallback UI
      this._renderErrorState();
    }
  }
  
  _needsFullRender(currentState) {
    const last = this._lastRenderedState;
    
    // Full render needed if:
    // - Different cocktail (name changed)
    // - DOM structure elements missing
    // Note: Removed collapsed check since it's now CSS-based
    return !last ||
           last.name !== currentState.name ||
           !this._elements.container ||
           !this.querySelector('.cocktail-card');
  }
  
  _performFullRender(state) {
    return performanceMonitor.timeRender(this._componentName || 'CocktailCard', () => {
      this._log.blue('Performing full render for:', state.name);
      
      // Clear and rebuild entire structure
      this._clearContent();
      
      // Always render full view - collapse handled by CSS
      this._renderFullView(state);
      
      // Cache DOM elements after full render
      this._cacheElements();
    }, 'full-render');
  }
  
  _performIncrementalUpdate(state) {
    return performanceMonitor.timeRender(this._componentName || 'CocktailCard', () => {
      this._log.blue('Performing incremental update for:', state.name);
      
      const last = this._lastRenderedState;
      
      // Update only changed elements
      if (last.isFavorited !== state.isFavorited) {
        this._updateFavoriteButton(state.isFavorited);
      }
      
      // Note: For this component, most changes require full re-render
      // because cocktail content (ingredients, instructions) are complex.
      // The main optimization is avoiding re-renders when only favorites change.
    }, 'incremental-update');
  }
  
  // CSS-based collapse state management - no re-rendering needed
  _updateCollapseState() {
    this._log.blue('Updating collapse state via CSS:', this._collapsed);
    
    // Use data attribute for CSS targeting
    this.dataset.collapsed = String(this._collapsed);
    
    // Performance monitoring for CSS-based toggle
    performanceMonitor.timeRender(this._componentName || 'CocktailCard', () => {
      // CSS handles the visual change - no DOM manipulation needed
    }, this._collapsed ? 'css-collapse' : 'css-expand');
  }
  
  _renderFullView(state) {
    // Process content using centralized utilities with HTML escaping
    const ingredientsList = ContentProcessor.processIngredients(state.ingredients, Validator.escapeHtml);
    const instructionsList = ContentProcessor.processInstructions(state.instructions, Validator.escapeHtml);
    
    // Escape all content
    const titleText = Validator.escapeHtml(state.name || 'Unnamed Cocktail');
    const categoryText = state.category ? Validator.escapeHtml(state.category) : '';
    const alcoholicText = state.alcoholic ? Validator.escapeHtml(state.alcoholic) : '';
    const glassText = state.glass ? Validator.escapeHtml(state.glass) : '';
    const imageAlt = Validator.escapeHtml(state.name || 'Cocktail image');

    this.innerHTML = `
      <div class="cocktail-card">
        <div class="card-header">
          <h3 class="card-title">${titleText}</h3>
          <button class="favorite-button ${state.isFavorited ? 'favorited' : ''}" type="button" title="${state.isFavorited ? 'Remove from favorites' : 'Add to favorites'}" aria-pressed="${state.isFavorited}">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="${state.isFavorited ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
            </svg>
          </button>
        </div>
        
        ${state.image ? `
          <div class="card-image">
            <img src="${Validator.escapeHtml(state.image)}" alt="${imageAlt}" loading="lazy">
          </div>
        ` : ''}
        
        <div class="card-content">
          ${state.category || state.alcoholic || state.glass ? `
            <div class="card-meta">
              ${state.category ? `<span class="meta-tag">${categoryText}</span>` : ''}
              ${state.alcoholic ? `<span class="meta-tag">${alcoholicText}</span>` : ''}
              ${state.glass ? `<span class="meta-tag">${glassText}</span>` : ''}
            </div>
          ` : ''}
          
          ${ingredientsList ? `
            <div class="card-section">
              <h4>Ingredients:</h4>
              <ul class="ingredients-list">
                ${ingredientsList}
              </ul>
            </div>
          ` : ''}
          
          ${instructionsList ? `
            <div class="card-section">
              <h4>Instructions:</h4>
              <div class="instructions">
                ${instructionsList}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
  
  _clearContent() {
    this.replaceChildren(); // Secure clear
    this._elements = {};
    this._lastRenderedState = null;
  }
  
  _renderErrorState() {
    this.innerHTML = `
      <div class="error-state">
        <h3>Card Error</h3>
        <p>Unable to display this cocktail</p>
      </div>
    `;
  }
  
  // Cache DOM elements after render to improve performance
  _cacheElements() {
    this._elements = {
      favoriteButton: this.querySelector('.favorite-button'),
      favoriteSvg: this.querySelector('.favorite-button svg')
    };
  }
}

customElements.define('cocktail-card', CocktailCard);
